/**
 * Mirrors Course.js's getYearOptions() method on the backend, so any form
 * that already has a fetched Course object (with durationYears) can build
 * the year dropdown without a second API round-trip.
 *
 * Kept in sync manually with server/models/Course.js — if the backend's
 * ordinal logic changes, update this too.
 */
function ordinalSuffix(n) {
    const remainder100 = n % 100;
    if (remainder100 >= 11 && remainder100 <= 13) return "th";
    switch (n % 10) {
        case 1:
            return "st";
        case 2:
            return "nd";
        case 3:
            return "rd";
        default:
            return "th";
    }
}

/**
 * @param {{ durationYears: number }} course
 * @returns {{ yearNumber: number, label: string }[]}
 */
export function getYearOptions(course) {
    if (!course?.durationYears) return [];
    const options = [];
    for (let y = 1; y <= course.durationYears; y++) {
        options.push({ yearNumber: y, label: `${y}${ordinalSuffix(y)} Year` });
    }
    return options;
}

/**
 * Generates the list of semesters for a course.
 * If yearNumber is specified, returns only the semesters for that year
 * (e.g. For a 4-year course with 2 sem/year, year 1 -> Sem 1 & 2; year 2 -> Sem 3 & 4).
 * If yearNumber is omitted, returns all semesters for the course (1..8).
 *
 * @param {{ durationYears: number, semestersPerYear?: number }} course
 * @param {number|string} [yearNumber]
 * @returns {{ semesterNumber: number, yearNumber: number, label: string }[]}
 */
export function getSemesterOptions(course, yearNumber) {
    if (!course?.durationYears) return [];
    const semestersPerYear = Number(course.semestersPerYear) || 2;
    const durationYears = Number(course.durationYears) || 1;

    if (yearNumber != null && yearNumber !== "") {
        const y = Number(yearNumber);
        if (isNaN(y) || y < 1) return [];
        const start = (y - 1) * semestersPerYear + 1;
        const end = y * semestersPerYear;
        const options = [];
        for (let s = start; s <= end; s++) {
            options.push({
                semesterNumber: s,
                yearNumber: y,
                label: `Semester ${s}`,
            });
        }
        return options;
    }

    const totalSemesters = durationYears * semestersPerYear;
    const options = [];
    for (let s = 1; s <= totalSemesters; s++) {
        const y = Math.ceil(s / semestersPerYear);
        options.push({
            semesterNumber: s,
            yearNumber: y,
            label: `Semester ${s}`,
        });
    }
    return options;
}